'use client';

import { Breadcrumb } from '@/components/common/breadcrumb';
import { menuBreadcrumbItems } from '@/config/menu-catalog';
import { PageHeader } from '@/components/common/page-header';
import { ExpandableCard } from '@/components/ui/expandable-card';
import { useOrganization } from '@/contexts/organization-context';
import { useToast } from '@/hooks/use-toast';
import { apiClient } from '@/lib/api-client';
import { PetrohqApiResponseListPump, Pump } from '@/lib/api/data-contracts';
import { useFilterStore } from '@/stores/filter-store';
import { useCallback, useEffect, useState } from 'react';
import * as RealGrid from 'realgrid';
import { RealGridReact, RGDataColumn, RGDataField } from 'realgrid-react';

export function NozzleManagement() {
  const { toast } = useToast();
  const [nozzles, setNozzles] = useState<Pump[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // 필터 상태 가져오기
  const { store } = useFilterStore();
  const { getAllStores } = useOrganization();

  const fetchNozzles = useCallback(async () => {
    setIsLoading(true);
    try {
      // 필터에서 선택된 store 값들을 사용, 없으면 모든 store 사용
      const storeCodeList = store.length > 0 ? store : getAllStores();

      const searchParams = new URLSearchParams();
      storeCodeList.forEach(storeCode => {
        searchParams.append('storeCodeList', storeCode);
      });

      const response = await apiClient.get('master/nozzle', {
        searchParams: searchParams,
      });
      const data = (await response.json()) as PetrohqApiResponseListPump;
      const nozzleData = data.result || [];
      setNozzles(nozzleData);
    } catch {
      toast({
        title: '조회 실패',
        description: '노즐 데이터를 불러오는 중 오류가 발생했습니다',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [store, toast, getAllStores]);

  // 초기 데이터 로드
  useEffect(() => {
    fetchNozzles();
  }, []);

  // 조회 버튼 클릭 핸들러
  const handleSearch = () => {
    fetchNozzles();
  };

  return (
    <ExpandableCard
      breadcrumb={
        <Breadcrumb items={menuBreadcrumbItems('nozzle')} />
      }
      title='노즐 목록'
      fill
      pageHeader={
        <PageHeader
          onSearch={handleSearch}
          isLoading={isLoading}
        />
      }
    >
      {/* 노즐 테이블 */}
      <div
        className='border rounded-lg flex-1 min-h-0'
      >
        <div className='w-full h-full'>
          <RealGridReact
            gridProps={{
              checkBar: { visible: false },
              stateBar: { visible: false },
              edit: { readOnly: true, editable: false },
              sorting: {
                enabled: true,
                showSortOrder: true,
                style: RealGrid.SortStyle.INCLUSIVE,
              },
            }}
            rows={nozzles}
          >
            <RGDataField
              fieldName={'compCd'}
              dataType={RealGrid.ValueType.TEXT}
            />
            <RGDataField
              fieldName={'storeCd'}
              dataType={RealGrid.ValueType.TEXT}
            />
            <RGDataField
              fieldName={'pumpNo'}
              dataType={RealGrid.ValueType.TEXT}
            />
            <RGDataField
              fieldName={'tkCode'}
              dataType={RealGrid.ValueType.TEXT}
            />
            <RGDataField
              fieldName={'oilCode'}
              dataType={RealGrid.ValueType.TEXT}
            />
            <RGDataField
              fieldName={'kindPrice'}
              dataType={RealGrid.ValueType.TEXT}
            />
            <RGDataField
              fieldName={'self'}
              dataType={RealGrid.ValueType.TEXT}
            />
            <RGDataField
              fieldName={'ctrlUse'}
              dataType={RealGrid.ValueType.TEXT}
            />
            <RGDataField
              fieldName={'pumpUse'}
              dataType={RealGrid.ValueType.TEXT}
            />
            <RGDataField
              fieldName={'odtCode'}
              dataType={RealGrid.ValueType.TEXT}
            />
            <RGDataField
              fieldName={'protocal'}
              dataType={RealGrid.ValueType.TEXT}
            />
            <RGDataField
              fieldName={'pnozzle'}
              dataType={RealGrid.ValueType.TEXT}
            />
            <RGDataField
              fieldName={'pmake'}
              dataType={RealGrid.ValueType.TEXT}
            />
            <RGDataField
              fieldName={'deviceno'}
              dataType={RealGrid.ValueType.TEXT}
            />
            <RGDataField
              fieldName={'scno'}
              dataType={RealGrid.ValueType.TEXT}
            />
            <RGDataField
              fieldName={'scType'}
              dataType={RealGrid.ValueType.TEXT}
            />
            <RGDataColumn fieldName={'storeCd'} header={{ text: '매장' }} />
            <RGDataColumn
              fieldName={'pumpNo'}
              header={{ text: '노즐' }}
              width={40}
            />
            <RGDataColumn
              fieldName={'tkCode'}
              header={{ text: '탱크코드' }}
              width={40}
            />
            <RGDataColumn
              fieldName={'oilCode'}
              header={{ text: '유종' }}
              width={60}
            />
            <RGDataColumn
              fieldName={'kindPrice'}
              header={{ text: '설정단가' }}
              width={60}
            />
            <RGDataColumn
              fieldName={'self'}
              header={{ text: '계기값' }}
              width={60}
            />
            <RGDataColumn
              fieldName={'ctrlUse'}
              header={{ text: '통제' }}
              width={60}
            />
            <RGDataColumn
              fieldName={'pumpUse'}
              header={{ text: '계기출력' }}
              width={60}
            />
            <RGDataColumn
              fieldName={'odtCode'}
              header={{ text: 'SELF/ODT' }}
              width={60}
            />
            <RGDataColumn
              fieldName={'protocal'}
              header={{ text: '프로토콜' }}
              width={60}
            />
            <RGDataColumn
              fieldName={'pnozzle'}
              header={{ text: '노즐IF' }}
              width={60}
            />
            <RGDataColumn
              fieldName={'pmake'}
              header={{ text: '셀프주유기' }}
              width={70}
            />
            <RGDataColumn
              fieldName={'deviceno'}
              header={{ text: '장비번호' }}
              width={60}
            />
            <RGDataColumn
              fieldName={'scno'}
              header={{ text: 'SC No.' }}
              width={60}
            />
            <RGDataColumn
              fieldName={'scType'}
              header={{ text: 'SCTYPE' }}
              width={60}
            />
          </RealGridReact>
        </div>
      </div>
    </ExpandableCard>
  );
}
